import { IGameData, HexGridType, IPopOutSpaceData, IBubbleGrid } from 'js/bubbleCore/types';
import { convertHexLimitsToSpaceLimits } from 'js/bubbleCore/utils/convertHexLimitsToSpaceLimits';
import { convertRectangleToSpace } from 'js/bubbleCore/utils/convertRectangleToSpace';
import { ISpace, IRectangle } from 'framework/utils/math-utils';
import { getHexSize } from 'js/bubbleCore/utils/getHexSize';
import { RandomDataGenerator } from 'framework/RandomDataGenerator/RandomDataGenerator';
import { BubbleFactory } from 'js/bubbleCore/bubbles/BubbleFactory';
import { parsePopOutSpace } from 'js/bubbleCore/levelParser/parsePopOutSpace';
import { parseGrid } from 'js/bubbleCore/levelParser/parseGrid';
import { parseShooter } from 'js/bubbleCore/levelParser/parseShooter';
import { IColorConfig } from 'js/bubbleCore/types';

export interface ILevelData {
  config: ILevelDataGameConfig;
  grid: ILevelDataGrid;
  popOutSpace: ILevelDataPopOutSpace;
  shooter: ILevelDataShooter;
}

interface ILevelDataGameConfig {
  fireEvery: number;
  availableColors: IColorConfig[],
}

interface ILevelDataGrid {
  type: HexGridType;
  hexLimits: IRectangle;
  asymetrical: boolean;
  rowsToFill: number;
  scrollSpeed: number;
  fillScrollSpeed: number;
  scrollFill: number;
  deathLineY: number;
}

// normalize for field
export interface ILevelDataPointZone {
  x: number;
  score: number;
}

interface ILevelDataShooter {
  bubbleSpeed: number;
  positionY: number;
  angleLimitDeg: number;
}

interface ILevelDataPopOutSpace {
  gravity: number;
  pointZones: ILevelDataPointZone[];
  popOutVelRange: ISpace;
  bouncers: {
    y: number;
    width: number;
    height: number;
  };
  pointZonesY: number;
}

export function parseLevel(
  data: IGameData,
  rnd: RandomDataGenerator,
  factory: BubbleFactory,
  levelData: ILevelData
): IGameData {
  const type = levelData.grid.type;

  const spaceLimits = convertRectangleToSpace(
    convertHexLimitsToSpaceLimits(levelData.grid.hexLimits, type)
  );

  if (!data) {
    data = {
      config: {
        fireEvery: levelData.config.fireEvery,
        availableColors: levelData.config.availableColors,
      },
      state: {
        score: 0,
        chain: 0,
        gameOver: false,
        firstShoot: false,
        time: 0,
      },
      shootingSpace: {
        type,
        limits: spaceLimits,
        values: [],
        gravity: 0,
      },
      popOutSpace: parsePopOutSpace(rnd, levelData),
      grid: parseGrid(factory, levelData),
      shooter: parseShooter(factory, levelData),
    };
  } else {
    let popOutSpace: IPopOutSpaceData = parsePopOutSpace(rnd, levelData);
    for (let i: number = 0; i < popOutSpace.bouncers.length; i++) {
      data.popOutSpace.bouncers[i].size = popOutSpace.bouncers[i].size;
    }
    for (let i: number = 0; i < popOutSpace.pointZones.length; i++) {
      data.popOutSpace.pointZones[i].rect = popOutSpace.pointZones[i].rect;
    }

    let grid: IBubbleGrid = parseGrid(factory, levelData);
    data.grid.deathLineY = grid.deathLineY;

    data.shooter.position = parseShooter(factory, levelData).position;
    data.shootingSpace.limits = spaceLimits;
  }
  return data;
}

